// @ts-nocheck
import React, { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Edit, Trash2 } from "lucide-react";
import { useStaffRoles } from "@/hooks/useStaffingData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

const payTypeColors: Record<string, string> = {
  hourly: "bg-blue-500 text-white",
  daily: "bg-green-500 text-white",
  flat: "bg-purple-500 text-white",
  volunteer: "bg-gray-400 text-white",
};

const emptyForm = { name: "", description: "", default_pay_rate: "", pay_type: "hourly", is_active: true };

const StaffRolesTab: React.FC = () => {
  const { data: roles, isLoading } = useStaffRoles();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);

  const handleOpen = (role?: any) => {
    if (role) {
      setEditId(role.id);
      setForm({
        name: role.name,
        description: role.description || "",
        default_pay_rate: role.default_pay_rate?.toString() || "",
        pay_type: role.pay_type || "hourly",
        is_active: role.is_active !== false,
      });
    } else {
      setEditId(null);
      setForm(emptyForm);
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const payload = {
      name: form.name,
      description: form.description || null,
      default_pay_rate: form.default_pay_rate ? parseFloat(form.default_pay_rate) : null,
      pay_type: form.pay_type,
      is_active: form.is_active,
    };
    if (editId) {
      const { error } = await supabase.from("manage_staff_roles").update(payload).eq("id", editId);
      if (error) { toast.error("Failed to update role"); return; }
      toast.success("Role updated");
    } else {
      const { error } = await supabase.from("manage_staff_roles").insert(payload);
      if (error) { toast.error("Failed to add role"); return; }
      toast.success("Role added");
    }
    setOpen(false);
    queryClient.invalidateQueries({ queryKey: ["staff-roles"] });
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this role?")) return;
    const { error } = await supabase.from("manage_staff_roles").delete().eq("id", id);
    if (error) { toast.error("Failed to delete"); return; }
    toast.success("Role deleted");
    queryClient.invalidateQueries({ queryKey: ["staff-roles"] });
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{roles?.length || 0} roles defined</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" onClick={() => handleOpen()}><Plus className="h-4 w-4 mr-1" />Add Role</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editId ? "Edit Role" : "Add Role"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div><Label>Default Pay Rate ($)</Label><Input type="number" step="0.01" value={form.default_pay_rate} onChange={(e) => setForm({ ...form, default_pay_rate: e.target.value })} /></div>
              <div><Label>Pay Type</Label>
                <Select value={form.pay_type} onValueChange={(v) => setForm({ ...form, pay_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="flat">Flat</SelectItem>
                    <SelectItem value="volunteer">Volunteer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label>Active</Label>
              </div>
              <Button onClick={handleSave} className="w-full">{editId ? "Update" : "Add"} Role</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Default Pay Rate</TableHead>
              <TableHead>Pay Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(roles || []).map((role: any) => (
              <TableRow key={role.id}>
                <TableCell className="font-medium">{role.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{role.description || "—"}</TableCell>
                <TableCell className="text-right">{role.default_pay_rate ? `$${role.default_pay_rate.toFixed(2)}` : "—"}</TableCell>
                <TableCell><Badge className={payTypeColors[role.pay_type] || ""}>{role.pay_type || "—"}</Badge></TableCell>
                <TableCell><Badge variant={role.is_active !== false ? "success" : "secondary"}>{role.is_active !== false ? "Active" : "Inactive"}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => handleOpen(role)}><Edit className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(role.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default StaffRolesTab;
